// Handles "Failed to fetch dynamically imported module" errors that occur when
// a user has an old version of the app open, and a deploy has removed the
// hashed chunks their bundle is trying to lazy-load (route change, locale switch).
//
// Strategy: reload once to pick up the new index.html + fresh chunk references.
// A sessionStorage guard with a timestamp prevents infinite reload loops when
// the error is caused by something other than a stale bundle (offline, CDN
// outage, ad-blocker).

const RELOAD_FLAG_KEY = 'chunk-reload-attempted'
const RELOAD_GUARD_WINDOW_MS = 10_000

const DYNAMIC_IMPORT_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
]

export function isDynamicImportError(error: unknown): boolean {
  if (!error) return false
  const err = error as { name?: unknown; message?: unknown }
  if (err.name === 'ChunkLoadError') return true
  const message = typeof err.message === 'string' ? err.message : ''
  if (!message) return false
  // Fast-path: unhandledrejection fires for every rejected promise; avoid
  // 3 regex tests when the message clearly isn't about module loading.
  const lower = message.toLowerCase()
  if (!lower.includes('module') && !lower.includes('import')) return false
  return DYNAMIC_IMPORT_PATTERNS.some((p) => p.test(message))
}

function reloadGuardActive(now: number, storage: Storage): boolean {
  const raw = storage.getItem(RELOAD_FLAG_KEY)
  if (!raw) return false
  const ts = Number(raw)
  if (Number.isNaN(ts)) return false
  return now - ts < RELOAD_GUARD_WINDOW_MS
}

export function shouldReload(
  error: unknown,
  now: number = Date.now(),
  storage: Storage = sessionStorage,
): boolean {
  if (!isDynamicImportError(error)) return false
  return !reloadGuardActive(now, storage)
}

export function markReloadAttempted(
  now: number = Date.now(),
  storage: Storage = sessionStorage,
): void {
  storage.setItem(RELOAD_FLAG_KEY, String(now))
}

export function clearReloadFlag(storage: Storage = sessionStorage): void {
  storage.removeItem(RELOAD_FLAG_KEY)
}

let installed = false

export function installChunkReloadHandler(): void {
  if (typeof window === 'undefined') return
  if (installed) return
  installed = true

  const reload = (error: unknown) => {
    if (!shouldReload(error)) return
    markReloadAttempted()
    window.location.reload()
  }

  // Vite fires this before the import() promise rejects on preload failure.
  window.addEventListener('vite:preloadError', (event) => {
    reload((event as Event & { payload?: unknown }).payload)
  })

  // Fallback for failure modes not covered by vite:preloadError (e.g. nested
  // dynamic imports like locale loaders inside an already-loaded chunk).
  window.addEventListener('unhandledrejection', (event) => {
    reload(event.reason)
  })

  // Successful page load = we're on the fresh bundle, clear the guard.
  // `pageshow` also fires on bfcache restore (Safari/Firefox back-forward);
  // `load` does not — so we listen to both.
  window.addEventListener('load', () => clearReloadFlag())
  window.addEventListener('pageshow', () => clearReloadFlag())
}
